import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import moment from "moment";

function ReporteContenedor() {
  const { user } = useContext(AuthContext);
  const URL_PAGOS_PROVEEDORES = process.env.REACT_APP_URL_PAGOS_PROVEEDORES;
  const URL_ANTICIPOS_PROVEEDORES =
    process.env.REACT_APP_URL_ANTICIPOS_PROVEEDORES;
  const URL_ARTICULOS_EMBARQUES = process.env.REACT_APP_URL_ARTICULOS_EMBARQUES;

  let hoy = new Date();
  let inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  let endDate = moment(hoy).format("YYYY-MM-DD");
  let startDate = moment(inicio).format("YYYY-MM-DD");

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [selectedContenedor, setSelectedContenedor] = useState("");
  // % adicional por contenedor (gastos/overhead), editable, default 7%
  const [porcentaje, setPorcentaje] = useState(7);

  const [renglones, setRenglones] = useState([]);
  const [contenedores, setContenedores] = useState([]);
  // Búsqueda libre por contenedor o por artículo (SKU / nombre)
  const [busqueda, setBusqueda] = useState("");
  // Artículos que vienen en cada embarque: { [idEmbarques]: [{SKU, nombre, cantidad}] }
  const [articulosPorEmbarque, setArticulosPorEmbarque] = useState({});
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const monedaMXN = (n) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(Number(n) || 0);

  const monedaUSD = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(n) || 0);

  // Conceptos que forman el costo de mercancía (lo demás son gastos: flete, cruce, etc.)
  const CONCEPTOS_MERCANCIA = ["FACTURA", "ANTICIPO", "LIQUIDACION"];
  const esMercancia = (concepto) =>
    CONCEPTOS_MERCANCIA.includes(String(concepto).trim().toUpperCase());

  // Regla de control: el costo real (Pagado) tiene prioridad; el simulado
  // solo se usa si no hay un pago real para ese mismo concepto.
  const filtrarRealPrioridad = (renglones) => {
    const hayReal = {};
    renglones.forEach((r) => {
      if (r.estado === "Pagado") hayReal[r.concepto] = true;
    });
    return renglones.filter((r) =>
      hayReal[r.concepto] ? r.estado === "Pagado" : true
    );
  };

  useEffect(() => {
    if (!selectedFechaInicio || !selectedFechaFin) return;
    showLoader();

    const pPagos = axios
      .get(
        `${URL_PAGOS_PROVEEDORES}Fechas/${selectedFechaInicio}/${selectedFechaFin}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        return response.data
          .filter((a) => a.is_active !== "No")
          .filter((a) => a.embarques && a.embarques.contenedor)
          .map((a) => ({
            fecha: a.fecha ? moment(a.fecha).format("YYYY-MM-DD") : "",
            contenedor: a.embarques.contenedor || "",
            pzs: Number(a.embarques.total_cantidad) || 0,
            idEmbarques: a.embarques.idEmbarques || "",
            concepto:
              a.conceptoNombre || a.conceptosPago?.concepto || "PAGO",
            montoUSD: Number(a.montoUSD) || 0,
            tipoCambio: Number(a.tipoCambio) || 0,
            totalMXN: Number(a.totalMXN) || 0,
            esB2B: a.esB2B === "Si",
            estado: a.estado || "Simulado",
          }));
      })
      .catch((err) => {
        console.log(err);
        return [];
      });

    const pAnticipos = axios
      .get(
        `${URL_ANTICIPOS_PROVEEDORES}Fechas/${selectedFechaInicio}/${selectedFechaFin}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        return response.data
          .filter((a) => a.is_active === "Si" && Number(a.importe) > 0)
          .filter((a) => a.embarques && a.embarques.contenedor)
          .map((a) => {
            const importe = Number(a.importe) || 0;
            const tipoCambio = Number(a.tipoCambio) || 0;
            const montoPesos =
              Number(a.montoPesos) > 0
                ? Number(a.montoPesos)
                : importe * tipoCambio;
            return {
              fecha: a.fecha ? moment(a.fecha).format("YYYY-MM-DD") : "",
              contenedor: a.embarques.contenedor || "",
              pzs: Number(a.embarques.total_cantidad) || 0,
              idEmbarques: a.embarques.idEmbarques || "",
              concepto: "ANTICIPO",
              montoUSD: importe,
              tipoCambio,
              totalMXN: montoPesos,
              esB2B: false,
              estado: "Pagado", // los anticipos son reales
            };
          });
      })
      .catch((err) => {
        console.log(err);
        return [];
      });

    Promise.all([pPagos, pAnticipos])
      .then(([pagos, anticipos]) => {
        const todos = [...pagos, ...anticipos];
        setRenglones(todos);
        const cont = [
          ...new Set(todos.map((r) => r.contenedor).filter(Boolean)),
        ].sort();
        setContenedores(cont);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  // Artículos que trae cada embarque (para mostrarlos y poder buscar por artículo)
  useEffect(() => {
    axios
      .get(URL_ARTICULOS_EMBARQUES, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        const map = {};
        (response.data || [])
          .filter((a) => a.is_active === "Si")
          .forEach((a) => {
            const idEmb = a.embarques?.[0]?.idEmbarques;
            const art = a.articulos?.[0];
            if (!idEmb || !art) return;
            if (!map[idEmb]) map[idEmb] = [];
            map[idEmb].push({
              SKU: art.SKU || "",
              nombre: art.nombre || "",
              cantidad: Number(a.cantidad) || 0,
            });
          });
        setArticulosPorEmbarque(map);
      })
      .catch((err) => console.log(err));
  }, []);

  // Agrupa por contenedor
  const grupos = useMemo(() => {
    let data = renglones;
    if (selectedContenedor) {
      data = data.filter((r) => r.contenedor === selectedContenedor);
    }

    const map = {};
    data.forEach((r) => {
      if (!map[r.contenedor]) {
        map[r.contenedor] = {
          contenedor: r.contenedor,
          pzs: r.pzs || 0,
          renglones: [],
          embarquesIds: [],
        };
      }
      // pzs comun del embarque; toma el mayor por si algun registro viene en 0
      if ((r.pzs || 0) > (map[r.contenedor].pzs || 0)) {
        map[r.contenedor].pzs = r.pzs || 0;
      }
      if (r.idEmbarques && !map[r.contenedor].embarquesIds.includes(r.idEmbarques)) {
        map[r.contenedor].embarquesIds.push(r.idEmbarques);
      }
      map[r.contenedor].renglones.push(r);
    });

    const pct = Number(porcentaje) || 0;
    const arr = Object.values(map).map((g) => {
      // aplica prioridad real>simulado antes de costear
      const rowsEfectivas = filtrarRealPrioridad(g.renglones);
      const rows = [...rowsEfectivas]
        .sort((a, b) => ((a.fecha || "") > (b.fecha || "") ? 1 : -1))
        // costo por pieza de cada pago (#2): monto MXN del pago / cantidad
        .map((r) => ({
          ...r,
          costoPza: g.pzs > 0 ? (Number(r.totalMXN) || 0) / g.pzs : 0,
        }));
      const subtotalMXN = rows.reduce((a, r) => a + (Number(r.totalMXN) || 0), 0);
      // Costo 1 = solo mercancía; Costo 2 = + gastos; Costo final = + 7%
      const mercanciaMXN = rows
        .filter((r) => esMercancia(r.concepto))
        .reduce((a, r) => a + (Number(r.totalMXN) || 0), 0);
      const gastosMXN = subtotalMXN - mercanciaMXN;
      const adicional = subtotalMXN * (pct / 100); // #4: 7% editable por contenedor
      const totalMXN = subtotalMXN + adicional;
      const costo1 = g.pzs > 0 ? mercanciaMXN / g.pzs : 0;
      const costo2 = g.pzs > 0 ? subtotalMXN / g.pzs : 0;
      const costoPorPza = g.pzs > 0 ? totalMXN / g.pzs : 0; // costo final
      // Artículos que trae el contenedor (juntando sus embarques y sumando repetidos)
      const artMap = {};
      g.embarquesIds.forEach((idEmb) => {
        (articulosPorEmbarque[idEmb] || []).forEach((a) => {
          const k = a.SKU || a.nombre;
          if (!artMap[k]) artMap[k] = { ...a };
          else artMap[k].cantidad += a.cantidad;
        });
      });
      const articulos = Object.values(artMap).sort((a, b) =>
        (a.SKU || "") > (b.SKU || "") ? 1 : -1
      );
      return {
        contenedor: g.contenedor,
        pzs: g.pzs,
        renglones: rows,
        articulos,
        mercanciaMXN,
        gastosMXN,
        subtotalMXN,
        adicional,
        totalMXN,
        costo1,
        costo2,
        costoPorPza,
      };
    });

    arr.sort((a, b) => (a.contenedor > b.contenedor ? 1 : -1));

    // Búsqueda libre: por contenedor o por artículo (SKU / nombre)
    const term = busqueda.trim().toLowerCase();
    if (!term) return arr;
    return arr.filter(
      (g) =>
        (g.contenedor || "").toLowerCase().includes(term) ||
        g.articulos.some(
          (a) =>
            (a.SKU || "").toLowerCase().includes(term) ||
            (a.nombre || "").toLowerCase().includes(term)
        )
    );
  }, [renglones, selectedContenedor, porcentaje, articulosPorEmbarque, busqueda]);

  // Totales generales (#4): suma de todos los contenedores
  const totalGeneral = grupos.reduce(
    (acc, g) => ({
      subtotal: acc.subtotal + (Number(g.subtotalMXN) || 0),
      adicional: acc.adicional + (Number(g.adicional) || 0),
      total: acc.total + (Number(g.totalMXN) || 0),
    }),
    { subtotal: 0, adicional: 0, total: 0 }
  );

  // Aplana los grupos para exportar
  function dataPlana() {
    const out = [];
    grupos.forEach((g) => {
      g.renglones.forEach((r) => {
        out.push({
          Contenedor: g.contenedor,
          PZS: g.pzs,
          Fecha: r.fecha,
          Concepto: r.concepto,
          MontoUSD: r.esB2B ? "-" : Number(r.montoUSD) || 0,
          TDC: r.esB2B ? "-" : Number(r.tipoCambio).toFixed(2),
          MontoMXN: Number(r.totalMXN) || 0,
          CostoPorPza: Number(r.costoPza) || 0,
        });
      });
    });
    return out;
  }

  function PDFTabla() {
    const data = dataPlana().map((a) => [
      a.Contenedor,
      new Intl.NumberFormat("en-US").format(a.PZS),
      a.Fecha,
      a.Concepto,
      a.MontoUSD === "-" ? "-" : monedaUSD(a.MontoUSD),
      a.TDC,
      monedaMXN(a.MontoMXN),
      monedaMXN(a.CostoPorPza),
    ]);
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 8, 45, 15);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 125, 5, 30, 30);
    doc.text(`Reporte por Contenedor`, 20, 20);
    doc.text(`${selectedFechaInicio} - ${selectedFechaFin}`, 20, 28);
    doc.autoTable({
      head: [
        [
          "Contenedor",
          "PZS",
          "Fecha Pago",
          "Concepto",
          "Monto USD",
          "TDC",
          "Monto MXN",
          "Costo x Pza",
        ],
      ],
      body: data,
      startY: 34,
    });
    doc.save(`ReporteContenedor.pdf`);
  }

  function excel() {
    const dataExcel = dataPlana();
    const option = {};
    option.fileName = "ReporteContenedor";
    option.datas = [
      {
        sheetData: dataExcel,
        sheetName: "ReporteContenedor",
        sheetFilter: [
          "Contenedor",
          "PZS",
          "Fecha",
          "Concepto",
          "MontoUSD",
          "TDC",
          "MontoMXN",
          "CostoPorPza",
        ],
        sheetHeader: [
          "Contenedor",
          "PZS",
          "Fecha",
          "Concepto",
          "MontoUSD",
          "TDC",
          "MontoMXN",
          "CostoPorPza",
        ],
      },
    ];
    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_compras ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuCompras"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Reporte por Contenedor</h3>
          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Contenedor</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedContenedor}
                onChange={(e) => setSelectedContenedor(e.target.value)}
              >
                <option value="">Todos</option>
                {contenedores.map((c) => (
                  <option value={c}>{c}</option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Buscar</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="Contenedor, SKU o artículo"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">% Adicional</Label>
              <Input
                bsSize="sm"
                type="number"
                step="any"
                min="0"
                value={porcentaje}
                onChange={(e) => setPorcentaje(e.target.value)}
              />
            </Col>
          </Row>
          <br />
          {grupos.length === 0 ? (
            <h5 align="center" style={{ color: "#64748b" }}>
              Sin pagos con contenedor en el periodo seleccionado
            </h5>
          ) : (
            <>
            {grupos.map((g) => (
              <div
                key={g.contenedor}
                className="card"
                style={{ marginBottom: 18, padding: 12 }}
              >
                <Row style={{ alignItems: "center" }}>
                  <Col md={3}>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      CONTENEDOR
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>
                      {g.contenedor}
                    </div>
                  </Col>
                  <Col md={2} className="text-center">
                    <div style={{ fontSize: 12, color: "#64748b" }}>PZS</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>
                      {new Intl.NumberFormat("en-US").format(g.pzs)}
                    </div>
                  </Col>
                  <Col md={2} className="text-center">
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      COSTO 1 <small>(mercancía)</small>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>
                      {monedaMXN(g.costo1)}
                    </div>
                  </Col>
                  <Col md={2} className="text-center">
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      COSTO 2 <small>(+ gastos)</small>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>
                      {monedaMXN(g.costo2)}
                    </div>
                  </Col>
                  <Col md={3} className="text-center">
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      COSTO FINAL <small>(+{Number(porcentaje) || 0}%)</small>
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#15803d",
                      }}
                    >
                      {monedaMXN(g.costoPorPza)}
                    </div>
                  </Col>
                </Row>
                {/* Artículos que trae el contenedor */}
                {g.articulos && g.articulos.length > 0 ? (
                  <div
                    style={{
                      marginTop: 10,
                      paddingTop: 8,
                      borderTop: "1px solid #e2e8f0",
                    }}
                  >
                    <div
                      style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}
                    >
                      ARTÍCULOS DEL CONTENEDOR ({g.articulos.length})
                    </div>
                    <div className="d-flex flex-wrap">
                      {g.articulos.map((a, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 12,
                            background: "#f1f5f9",
                            borderRadius: 12,
                            padding: "2px 10px",
                            margin: "0 6px 6px 0",
                          }}
                          title={a.nombre}
                        >
                          <strong>{a.SKU}</strong>
                          {a.nombre ? ` · ${a.nombre}` : ""} ·{" "}
                          {new Intl.NumberFormat("en-US").format(a.cantidad)} pz
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                <br />
                <Table
                  size="sm"
                  striped
                  borderless
                  className="table-responsive-xl"
                >
                  <thead>
                    <tr>
                      <th>Fecha Pago</th>
                      <th>Concepto</th>
                      <th className="text-right">Monto USD</th>
                      <th className="text-right">TDC</th>
                      <th className="text-right">Monto MXN</th>
                      <th className="text-right">Costo / Pza</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.renglones.map((r, i) => (
                      <tr key={i}>
                        <td style={{ minWidth: 100 }}>{r.fecha}</td>
                        <td>{r.concepto}</td>
                        <td className="text-right">
                          {r.esB2B ? "-" : monedaUSD(r.montoUSD)}
                        </td>
                        <td className="text-right">
                          {r.esB2B ? "-" : Number(r.tipoCambio).toFixed(2)}
                        </td>
                        <td className="text-right">{monedaMXN(r.totalMXN)}</td>
                        <td className="text-right">{monedaMXN(r.costoPza)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="text-right">
                        Subtotal
                      </td>
                      <td className="text-right">{monedaMXN(g.subtotalMXN)}</td>
                      <td className="text-right">
                        {monedaMXN(g.pzs > 0 ? g.subtotalMXN / g.pzs : 0)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="text-right">
                        + {Number(porcentaje) || 0}% adicional
                      </td>
                      <td className="text-right">{monedaMXN(g.adicional)}</td>
                      <td className="text-right">
                        {monedaMXN(g.pzs > 0 ? g.adicional / g.pzs : 0)}
                      </td>
                    </tr>
                    <tr
                      style={{
                        fontWeight: 700,
                        borderTop: "2px solid #cbd5e1",
                      }}
                    >
                      <td colSpan={4} className="text-right">
                        TOTAL MXN
                      </td>
                      <td className="text-right">{monedaMXN(g.totalMXN)}</td>
                      <td className="text-right" style={{ color: "#15803d" }}>
                        {monedaMXN(g.costoPorPza)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            ))}
            {/* Total general (#4) */}
            <div
              className="card"
              style={{ padding: 12, background: "#f8fafc" }}
            >
              <Row style={{ alignItems: "center" }}>
                <Col md={4}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    TOTAL GENERAL ({grupos.length} contenedor
                    {grupos.length === 1 ? "" : "es"})
                  </div>
                </Col>
                <Col md={3} className="text-center">
                  <div style={{ fontSize: 12, color: "#64748b" }}>Subtotal</div>
                  <div style={{ fontWeight: 700 }}>
                    {monedaMXN(totalGeneral.subtotal)}
                  </div>
                </Col>
                <Col md={2} className="text-center">
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    + {Number(porcentaje) || 0}%
                  </div>
                  <div style={{ fontWeight: 700 }}>
                    {monedaMXN(totalGeneral.adicional)}
                  </div>
                </Col>
                <Col md={3} className="text-center">
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    TOTAL MXN
                  </div>
                  <div
                    style={{ fontSize: 20, fontWeight: 700, color: "#15803d" }}
                  >
                    {monedaMXN(totalGeneral.total)}
                  </div>
                </Col>
              </Row>
            </div>
            </>
          )}
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default ReporteContenedor;
